<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { set } from 'lodash-es';
import {
  computed,
  ref,
} from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import { useToastStore } from '@modules/toast/store';
import { useSaveTranslationMutation } from '@modules/translate-manager/mutations';
import { watchImmediate } from '@vueuse/core';

import type { ITranslateLeafNodeProps } from './types';

const props = defineProps<ITranslateLeafNodeProps>();

const { t, i18next } = useTranslation();
const { mutateAsync, isLoading } = useSaveTranslationMutation();
const { displayPresetToast } = useToastStore();

const placeholderValue = computed(() => {
  const value = t(props.node.path, { lng: props.fallbackLanguage, fallbackLng: props.fallbackLanguage });
  return value === props.node.path ? '<!-- translate is missing -->' : value;
});

const initValue = computed(() => (
  t(props.node.path, { lng: props.selectedLanguage, fallbackLng: props.selectedLanguage })
));

const value = ref('');

const showButtons = computed(() => !!value.value && value.value !== initValue.value);

async function save(): Promise<void> {
  const bundle = set({}, props.node.path, value.value);

  await mutateAsync({
    language: props.selectedLanguage,
    new_localisation_data: bundle,
  });
  i18next.addResourceBundle(props.selectedLanguage, 'default', bundle, true, true);
  i18next.emit('changedResources');
  displayPresetToast('edited');
}

function clear(): void {
  value.value = initValue.value === props.node.path ? '' : initValue.value;
}

watchImmediate(initValue, (newValue) => {
  value.value = newValue === props.node.path ? '' : newValue;
});
</script>

<template>
  <div v-data-qa-auto-id="'VTranslateLeafNode'" class="flex flex-col justify-start lg:flex-row lg:items-center lg:gap-4">
    <div class="py-2 word-break lg:py-0 lg:pl-2">
      {{ node.label }}
    </div>
    <div class="flex grow flex-col items-center lg:flex-row">
      <PInputText
        v-model="value"
        :placeholder="placeholderValue"
        :disabled="isLoading"
        size="small"
        class="mr-2 h-[28px] w-full px-[5px] py-[10px] text-base lg:w-auto lg:min-w-[50%]"
      />
      <div
        v-show="showButtons"
        class="mt-2 flex lg:mt-0"
      >
        <VIconButton
          :loading="isLoading"
          icon="pi-check"
          severity="success"
          class="ml-0 flex-none lg:ml-2"
          @click="save"
        />
        <VIconButton
          :disabled="isLoading"
          icon="pi-times"
          severity="danger"
          class="ml-4 flex-none lg:ml-2"
          @click="clear"
        />
      </div>
    </div>
  </div>
</template>
